import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  countExtractedFields,
  isTombstoneComplete,
  isValidPostalCode,
  OWNER_TYPES,
  type ExtractionMismatch,
  type OwnerType,
  PARCEL_DETAIL_KEYS,
  parcelDetailOf,
  type ParcelInputDto,
  type SnbDocumentKind,
  type UpdateFileDto,
  addRecordPids,
} from '@munifocus/contracts-gov';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  Link,
  MenuItem,
  Stack,
  StatusChip,
  TagChip,
  TextField,
  Tooltip,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import { AppShell } from '../../components/AppShell';
import { ExtractedFieldRow } from '../../components/tombstone/ExtractedFieldRow';
import { SnbUploadPanel } from '../../components/tombstone/SnbUploadPanel';
import { useFile } from '../../lib/files-api';
import {
  useApplyExtraction,
  useExtractForFile,
  useExtractionDraft,
  useExtractions,
  useTombstoneOverrides,
} from '../../lib/property-extraction-api';
import { FileLoadError } from '../../components/FileLoadError';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Stage 1 property and owner identification, after the file exists.
 *
 * This is the "desk-completed" half of the intake split: an officer opens a file
 * in the field with nothing but a civic address, then finishes identifying the
 * property here — uploading the Service New Brunswick records, reviewing what
 * was read off them, and correcting anything wrong.
 *
 * It also closes a real gap. Until now `FileDetail` rendered these fields
 * read-only even though the API has always accepted updates to every one of
 * them, so there was no way to fix a tombstone after intake.
 */

interface FormState {
  address: string;
  pan: string;
  county: string;
  propertyDescription: string;
  taxingAuthority: string;
  harmonizationStatus: string;
  ownerName: string;
  ownerType: OwnerType | '';
  mailingAddress: string;
  postalCode: string;
  taxClass: string;
  neighbourhood: string;
  /**
   * Full parcel rows, not bare PID strings. The SNB Parcel record carries detail
   * the PAN record does not — parish, land titles status, area, interest type,
   * civic address parts — and `gov_parcel` has a column for each. `files.service`
   * assigns `input.parish ?? null` on every save, so submitting `{pid, isPrimary}`
   * alone does not merely skip those columns, it clears them.
   */
  parcels: ParcelInputDto[];
}

function mismatchFor(
  mismatches: ExtractionMismatch[],
  field: ExtractionMismatch['field'],
): { panValue: string | null; parcelValue: string | null } | null {
  const found = mismatches.find((m) => m.field === field);
  return found ? { panValue: found.panValue, parcelValue: found.parcelValue } : null;
}

export function TombstoneEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id = '' } = useParams();

  const file = useFile(id);
  // Asked once the file itself has loaded: for a file out of reach these would
  // only repeat its 404 three times over.
  const loadedId = file.data ? id : undefined;
  const draft = useExtractionDraft(loadedId);
  const runs = useExtractions(loadedId);
  const overrides = useTombstoneOverrides(loadedId);
  const extract = useExtractForFile(id);
  const apply = useApplyExtraction(id);

  const [form, setForm] = useState<FormState | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed from the file, then let anything the records proposed fill the blanks.
  // The officer's own entries win: a value already on the file was typed by a
  // person, and an extraction should not quietly overwrite it.
  //
  // Both queries have to have settled first. The file resolves faster than the
  // draft, and seeding on the file alone would fill every extraction-only field
  // with an empty string — which then reads as the officer having deliberately
  // cleared it, badging untouched fields "Changed" and logging phantom overrides
  // on save.
  useEffect(() => {
    if (!file.data || form !== null || draft.isLoading) return;
    const d = draft.data;
    setForm({
      address: file.data.address ?? d?.address ?? '',
      pan: file.data.pan ?? d?.pan ?? '',
      county: file.data.county ?? d?.county ?? '',
      propertyDescription: file.data.propertyDescription ?? d?.propertyDescription ?? '',
      taxingAuthority: file.data.taxingAuthority ?? d?.taxingAuthority ?? '',
      harmonizationStatus: file.data.harmonizationStatus ?? d?.harmonizationStatus ?? '',
      ownerName: file.data.ownerName ?? d?.ownerName ?? '',
      ownerType: (file.data.ownerType ?? d?.ownerType ?? '') as OwnerType | '',
      mailingAddress: file.data.mailingAddress ?? d?.mailingAddress ?? '',
      postalCode: file.data.postalCode ?? d?.postalCode ?? '',
      taxClass: file.data.taxClass ?? d?.taxClass ?? '',
      neighbourhood: file.data.neighbourhood ?? d?.neighbourhood ?? '',
      // The file's own parcels win on identity, but their detail columns are
      // blank until a Parcel record has been read — and the record is usually
      // uploaded after the PID was typed at intake. So fill each parcel's blanks
      // from the matching draft parcel rather than treating "file has parcels"
      // as "nothing left to learn".
      parcels:
        file.data.parcels.length > 0
          ? file.data.parcels.map((p) => {
              const saved: ParcelInputDto = {
                id: p.id,
                pid: p.pid,
                isPrimary: p.isPrimary,
                ...parcelDetailOf({
                  pid: p.pid,
                  county: p.county ?? undefined,
                  area: p.area ?? undefined,
                  areaUnit: p.areaUnit ?? undefined,
                  landTitlesStatus: p.landTitlesStatus ?? undefined,
                  landGazette: p.landGazette ?? undefined,
                  interestType: p.interestType ?? undefined,
                  civicNumber: p.civicNumber ?? undefined,
                  streetName: p.streetName ?? undefined,
                  streetType: p.streetType ?? undefined,
                  streetDirection: p.streetDirection ?? undefined,
                  placeName: p.placeName ?? undefined,
                  parish: p.parish ?? undefined,
                }),
              };
              const fromRecord = d?.parcels.find((dp) => dp.pid.trim() === p.pid.trim());
              return fromRecord ? { ...parcelDetailOf(fromRecord), ...saved } : saved;
            })
          : (d?.parcels ?? []),
    });
  }, [file.data, draft.data, draft.isLoading, form]);

  const mismatches = draft.data?.mismatches ?? [];
  const multiPid = draft.data?.multiPid;

  const completeness = useMemo(() => {
    if (!form || !file.data) return null;
    return isTombstoneComplete(
      {
        address: form.address,
        pan: form.pan,
        county: form.county,
        propertyType: file.data.propertyType,
        ownerName: form.ownerName,
        ownerType: form.ownerType || null,
        ownerUnknown: file.data.ownerUnknown,
      },
      form.parcels.filter((p) => p.pid.trim() !== '').map((p) => ({ pid: p.pid })),
    );
  }, [form, file.data]);

  // Before the early returns below: hooks cannot follow them.
  const postalCodeRef = useRef<HTMLInputElement | null>(null);

  // The failure first: the form is seeded only from a loaded file, so with the
  // file out of reach the loading branch below used to spin forever.
  if (file.isError) {
    return (
      <AppShell>
        <FileLoadError error={file.error} />
      </AppShell>
    );
  }

  if (file.isLoading || !form || !file.data) {
    return (
      <AppShell>
        <CircularProgress />
      </AppShell>
    );
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  // A malformed postal code is never a correction — it is a typo, and it would
  // send a statutory notice nowhere. Blocked at save rather than logged as an
  // override, unlike the fields where the officer may legitimately disagree with
  // the record.
  const postalCodeInvalid = !isValidPostalCode(form.postalCode);

  /** What the Parcel record itself said for a PID, for the "Record says" line. */
  const recordParcelFor = (pid: string) =>
    draft.data?.parcels.find((p) => p.pid.trim() === pid.trim()) ?? null;

  const uploadedRecords = (runs.data ?? []).filter((run) => run.status === 'succeeded');
  const extractedKinds = uploadedRecords.map((run) => run.kind as SnbDocumentKind);

  async function handleSave() {
    if (!form) return;
    const patch: UpdateFileDto = {
      address: form.address,
      pan: form.pan,
      county: form.county,
      propertyDescription: form.propertyDescription,
      taxingAuthority: form.taxingAuthority,
      harmonizationStatus: form.harmonizationStatus,
      ownerName: form.ownerName,
      mailingAddress: form.mailingAddress,
      // Trimmed: a postal code is pattern-validated, so the whitespace left by
      // clearing the field would otherwise be rejected as a malformed code.
      postalCode: form.postalCode.trim(),
      taxClass: form.taxClass,
      neighbourhood: form.neighbourhood,
      // Send each parcel whole. Trimming to `{pid, isPrimary}` here would blank
      // parish, land titles status, area and the civic address on every save.
      parcels: form.parcels
        .map((p) => ({ ...p, pid: p.pid.trim() }))
        .filter((p) => p.pid !== '')
        .map((p, index) => ({ ...p, isPrimary: index === 0 })),
    };
    if (form.ownerType) patch.ownerType = form.ownerType;

    // The inline alerts below stay — they persist the override count while the
    // officer keeps working. The toast is the transient "it landed" signal, and
    // it is the only feedback visible when the save button is scrolled away.
    try {
      const result = await apply.mutateAsync(patch);
      setSaved(true);
      // Name the file: this save function runs from a screen the officer may
      // have several of open, and "Saved." names none of them.
      const reference = file.data?.reference ?? '';
      snackbar.success(
        result.overrides.length === 0
          ? t('gov.extraction.savedNoOverrides', { reference })
          : t('gov.extraction.savedWithOverrides', {
              count: result.overrides.length,
              reference,
            }),
      );
    } catch {
      snackbar.error(t('gov.extraction.saveFailedToast'));
    }
  }

  return (
    <AppShell back={{ label: file.data.reference, onClick: () => navigate(`/files/${id}`) }}>
      <Stack spacing={2.5}>
        <Box>
          {/* Which file, and where in the workflow. Without this the page reads as
              a free-floating form: the topbar title names the task but not the
              subject, and the address is the only thing that identifies it. */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant="h5">{t('gov.extraction.tombstone.title')}</Typography>
            <TagChip label={t('gov.stages.intake')} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {file.data.reference} · {file.data.address}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('gov.extraction.tombstone.subtitle')}
          </Typography>
        </Box>
        <HairlineCard sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('gov.extraction.upload.title')}
          </Typography>
          <SnbUploadPanel
            done={extractedKinds}
            disabled={extract.isPending}
            onUpload={async (document, kind) => {
              const run = await extract.mutateAsync({ document, kind });
              snackbar.success(
                t('gov.extraction.extractedToast', {
                  kind: t(
                    kind === 'snb_pan'
                      ? 'gov.extraction.upload.kindPan'
                      : 'gov.extraction.upload.kindParcel',
                  ),
                }),
              );
              // Re-seed from the newly extracted record, keeping anything typed.
              const next = await draft.refetch();
              const d = next.data;
              if (!d) return;
              setForm((prev) =>
                prev
                  ? {
                      ...prev,
                      address: prev.address || (d.address ?? ''),
                      pan: prev.pan || (d.pan ?? ''),
                      county: prev.county || (d.county ?? ''),
                      propertyDescription:
                        prev.propertyDescription || (d.propertyDescription ?? ''),
                      taxingAuthority: prev.taxingAuthority || (d.taxingAuthority ?? ''),
                      harmonizationStatus:
                        prev.harmonizationStatus || (d.harmonizationStatus ?? ''),
                      ownerName: prev.ownerName || (d.ownerName ?? ''),
                      ownerType: prev.ownerType || ((d.ownerType ?? '') as OwnerType | ''),
                      mailingAddress: prev.mailingAddress || (d.mailingAddress ?? ''),
                      postalCode: prev.postalCode || (d.postalCode ?? ''),
                      taxClass: prev.taxClass || (d.taxClass ?? ''),
                      neighbourhood: prev.neighbourhood || (d.neighbourhood ?? ''),
                      // A Parcel record uploaded after the PAN is the only source
                      // of parish/land-titles detail, so merge it onto the PIDs
                      // already listed rather than keeping the bare list.
                      // Every other PID the PAN lists is added beside them.
                      parcels: addRecordPids(
                        prev.parcels.map((existing) => {
                          const match = d.parcels.find((p) => p.pid.trim() === existing.pid.trim());
                          return match ? { ...existing, ...parcelDetailOf(match) } : existing;
                        }),
                        d.parcels,
                      ),
                    }
                  : prev,
              );
              return {
                fieldsRead: countExtractedFields(run.extracted),
                warnings: run.warnings,
              };
            }}
          />

          {/* The uploaded PDFs themselves. An officer reviewing a value against
              "what the record said" needs the record, not just the chip saying it
              was read. `downloadUrl` is a signed S3 URL the API already returns. */}
          {uploadedRecords.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('gov.extraction.upload.records')}
              </Typography>
              <Stack spacing={1}>
                {uploadedRecords.map((run) => {
                  const kindLabel = t(
                    run.kind === 'snb_pan'
                      ? 'gov.extraction.upload.kindPan'
                      : 'gov.extraction.upload.kindParcel',
                  );
                  return (
                    // `space-between` rather than `ml: 'auto'` on the link: MUI's
                    // Stack spacing sets margin-left on every child through a
                    // descendant selector that outranks the child's own sx, so an
                    // auto margin there is silently overridden.
                    <Stack
                      key={run.id}
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        minHeight: 32,
                      }}
                    >
                      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            color: 'text.secondary',
                          }}
                        >
                          <Icons.FileText size={iconSize.md} />
                        </Box>
                        {/* Fixed label column so the dates line up row to row. */}
                        <Typography variant="body2" sx={{ lineHeight: 1.5, minWidth: 104 }}>
                          {kindLabel}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {frenchFirstOfMonth(new Date(run.createdAt).toLocaleString())}
                        </Typography>
                      </Stack>
                      {run.downloadUrl && (
                        <Link
                          href={run.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          aria-label={t('gov.extraction.upload.openAria', { kind: kindLabel })}
                          sx={{ lineHeight: 1.5, flexShrink: 0 }}
                        >
                          {t('gov.extraction.upload.open')}
                        </Link>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </>
          )}
        </HairlineCard>

        {mismatches.length > 0 && (
          <Alert severity="warning">
            {t('gov.extraction.mismatchBanner', { count: mismatches.length })}
          </Alert>
        )}

        {multiPid?.defaultYes && (
          <Alert severity="warning" icon={<Icons.AlertTriangle size={iconSize.lg} />}>
            <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
              {t('gov.extraction.multiPidBannerTitle')}
            </AlertTitle>
            {t('gov.extraction.multiPidBanner', {
              pids: multiPid.suggestedPids.join(', '),
            })}
          </Alert>
        )}

        <HairlineCard sx={{ p: 2.5 }}>
          <Typography variant="h6">{t('gov.extraction.tombstone.property')}</Typography>
          <Divider sx={{ my: 1 }} />

          <ExtractedFieldRow
            label={t('gov.file.address')}
            extracted={draft.data?.address ?? null}
            value={form.address}
            onChange={(v) => set('address', v)}
            source={draft.data?.address ? 'snb_pan' : null}
            // The PAN's address line against the Parcel record's civic number
            // and street name. Two properties of one landlord agree on county
            // and owner, so this is the check that catches the wrong *pair* of
            // records rather than a wrong value.
            mismatch={mismatchFor(mismatches, 'location')}
          />
          <ExtractedFieldRow
            label={t('gov.file.pan')}
            extracted={draft.data?.pan ?? null}
            value={form.pan}
            onChange={(v) => set('pan', v)}
            source={draft.data?.pan ? 'snb_pan' : null}
            mismatch={mismatchFor(mismatches, 'pan')}
          />
          <ExtractedFieldRow
            label={t('gov.file.county')}
            extracted={draft.data?.county ?? null}
            value={form.county}
            onChange={(v) => set('county', v)}
            source={draft.data?.county ? 'snb_pan' : null}
            mismatch={mismatchFor(mismatches, 'county')}
            helperText={t('gov.extraction.field.countyHint')}
          />
          <ExtractedFieldRow
            label={t('gov.file.propertyDescription')}
            extracted={draft.data?.propertyDescription ?? null}
            value={form.propertyDescription}
            onChange={(v) => set('propertyDescription', v)}
            source={draft.data?.propertyDescription ? 'snb_pan' : null}
          />
          <ExtractedFieldRow
            label={t('gov.file.taxingAuthority')}
            extracted={draft.data?.taxingAuthority ?? null}
            value={form.taxingAuthority}
            onChange={(v) => set('taxingAuthority', v)}
            source={draft.data?.taxingAuthority ? 'snb_pan' : null}
          />
          <ExtractedFieldRow
            label={t('gov.file.taxClass')}
            extracted={draft.data?.taxClass ?? null}
            value={form.taxClass}
            onChange={(v) => set('taxClass', v)}
            source={draft.data?.taxClass ? 'snb_pan' : null}
          />
          <ExtractedFieldRow
            label={t('gov.file.neighbourhood')}
            extracted={draft.data?.neighbourhood ?? null}
            value={form.neighbourhood}
            onChange={(v) => set('neighbourhood', v)}
            source={draft.data?.neighbourhood ? 'snb_pan' : null}
          />
          <ExtractedFieldRow
            label={t('gov.file.harmonization')}
            extracted={draft.data?.harmonizationStatus ?? null}
            value={form.harmonizationStatus}
            onChange={(v) => set('harmonizationStatus', v)}
            source={draft.data?.harmonizationStatus ? 'snb_pan' : null}
            multiline
          />
        </HairlineCard>

        <HairlineCard sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h6">{t('gov.intake.tombstone.title')}</Typography>
            <TagChip label={form.parcels.length} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('gov.extraction.pidsHint')}
          </Typography>
          <Divider sx={{ my: 1 }} />

          <Stack spacing={2}>
            {form.parcels.map((parcel, index) => {
              return (
                // Each parcel is its own bounded block. With twelve detail fields
                // per PID, an unbroken run of inputs gives no clue where one
                // parcel ends and the next begins — and picking the wrong PID's
                // parish is exactly the kind of error a notice carries forward.
                <HairlineCard key={parcel.id ?? index} surface="cream" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('gov.intake.tombstone.pidLabel', { n: index + 1 })}
                      value={parcel.pid}
                      onChange={(e) => {
                        const next = [...form.parcels];
                        next[index] = { ...next[index], pid: e.target.value };
                        set('parcels', next);
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label={t('gov.intake.tombstone.removePid')}
                      onClick={() =>
                        set(
                          'parcels',
                          form.parcels.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Icons.Trash2 size={iconSize.md} />
                    </IconButton>
                  </Stack>

                  {/* Detail from the SNB Parcel record, every field editable.
                      These are read off a document that can be wrong or stale, and
                      they end up in a registered notice, so the officer overrules
                      them the same way they overrule the assessment fields. */}
                  <Divider sx={{ my: 1.5 }} />
                  <Grid
                    container
                    spacing={1.5}
                    // Same rhythm as the intake form: the gap under the PID
                    // matches the gaps between the detail rows.
                    sx={{ mt: 1.5, pr: 5 }}
                  >
                    {PARCEL_DETAIL_KEYS.map((key) => {
                      const fromRecord = recordParcelFor(parcel.pid)?.[key] ?? null;
                      const current = parcel[key] ?? '';
                      const isChanged = (fromRecord ?? '') !== current;
                      return (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t(`gov.parcel.${key}`)}
                            value={current}
                            onChange={(e) => {
                              const next = [...form.parcels];
                              next[index] = { ...next[index], [key]: e.target.value };
                              set('parcels', next);
                            }}
                            helperText={
                              isChanged && fromRecord
                                ? t('gov.extraction.field.wasExtracted', { value: fromRecord })
                                : undefined
                            }
                            slotProps={{
                              input: {
                                endAdornment:
                                  isChanged && fromRecord ? (
                                    <Tooltip title={t('gov.extraction.field.revert')}>
                                      <IconButton
                                        size="small"
                                        aria-label={t('gov.extraction.field.revertAria', {
                                          field: t(`gov.parcel.${key}`),
                                        })}
                                        onClick={() => {
                                          const next = [...form.parcels];
                                          next[index] = { ...next[index], [key]: fromRecord };
                                          set('parcels', next);
                                        }}
                                      >
                                        <Icons.RotateCcw size={iconSize.sm} />
                                      </IconButton>
                                    </Tooltip>
                                  ) : undefined,
                              },
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </HairlineCard>
              );
            })}
            <Box>
              <Button
                size="small"
                startIcon={<Icons.Plus size={iconSize.md} />}
                onClick={() => set('parcels', [...form.parcels, { pid: '', isPrimary: false }])}
              >
                {t('gov.intake.tombstone.addPid')}
              </Button>
            </Box>
          </Stack>
        </HairlineCard>

        <HairlineCard sx={{ p: 2.5 }}>
          <Typography variant="h6">{t('gov.extraction.tombstone.owner')}</Typography>
          <Divider sx={{ my: 1 }} />

          <ExtractedFieldRow
            label={t('gov.file.ownerName')}
            extracted={draft.data?.ownerName ?? null}
            value={form.ownerName}
            onChange={(v) => set('ownerName', v)}
            source={draft.data?.ownerName ? 'snb_pan' : null}
            mismatch={mismatchFor(mismatches, 'ownerName')}
            disabled={file.data.ownerUnknown}
          />

          <Box sx={{ py: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('gov.file.ownerType')}
              {draft.data?.ownerTypeInferred && (
                <StatusChip
                  status="warning"
                  label={t('gov.extraction.field.inferred')}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.ownerType}
              disabled={file.data.ownerUnknown}
              onChange={(e) => set('ownerType', e.target.value as OwnerType)}
              helperText={
                draft.data?.ownerTypeInferred ? t('gov.extraction.field.ownerTypeHint') : undefined
              }
              slotProps={{ htmlInput: { 'aria-label': t('gov.file.ownerType') } }}
            >
              <MenuItem value="">—</MenuItem>
              {OWNER_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`gov.ownerType.${type}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <ExtractedFieldRow
            label={t('gov.file.mailingAddress')}
            extracted={draft.data?.mailingAddress ?? null}
            value={form.mailingAddress}
            onChange={(v) => set('mailingAddress', v)}
            source={draft.data?.mailingAddress ? 'snb_pan' : null}
            disabled={file.data.ownerUnknown}
          />
          <ExtractedFieldRow
            label={t('gov.file.postalCode')}
            extracted={draft.data?.postalCode ?? null}
            value={form.postalCode}
            onChange={(v) => set('postalCode', v)}
            source={draft.data?.postalCode ? 'snb_pan' : null}
            disabled={file.data.ownerUnknown}
            error={postalCodeInvalid ? t('gov.file.postalCodeInvalid') : undefined}
            inputRef={postalCodeRef}
          />
        </HairlineCard>

        {completeness && !completeness.complete && (
          <Alert severity="info">
            {t('gov.extraction.incomplete', {
              fields: completeness.missing
                .map((field) => t(`gov.extraction.missing.${field}`))
                .join(', '),
            })}
          </Alert>
        )}

        {saved && apply.data && (
          <Alert severity="success" onClose={() => setSaved(false)}>
            {apply.data.overrides.length === 0
              ? t('gov.extraction.savedNoOverrides', { reference: file.data.reference })
              : t('gov.extraction.savedWithOverrides', {
                  count: apply.data.overrides.length,
                  reference: file.data.reference,
                })}
          </Alert>
        )}

        {apply.isError && <Alert severity="error">{t('gov.extraction.saveFailed')}</Alert>}

        {(overrides.data?.length ?? 0) > 0 && (
          <HairlineCard sx={{ p: 2.5 }}>
            <Typography variant="h6">{t('gov.extraction.overrideLog')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('gov.extraction.overrideLogHint')}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              {overrides.data?.map((entry) => (
                <Box key={entry.id}>
                  <Typography variant="body2">
                    <strong>{t(`gov.extraction.missing.${entry.fieldKey}`, entry.fieldKey)}</strong>
                    {': '}
                    {entry.extractedValue ?? '—'} → {entry.acceptedValue ?? '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.extraction.overrideBy', {
                      name: entry.actorName ?? '—',
                      date: frenchFirstOfMonth(new Date(entry.createdAt).toLocaleString()),
                    })}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </HairlineCard>
        )}

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            // Live rather than disabled: the postal code says what is wrong as it
            // is typed, and a save with it wrong puts the cursor back there.
            onClick={() => {
              if (postalCodeInvalid) {
                postalCodeRef.current?.focus();
                return;
              }
              void handleSave();
            }}
            disabled={apply.isPending}
          >
            {apply.isPending ? t('gov.extraction.saving') : t('gov.extraction.save')}
          </Button>
          <Button variant="text" onClick={() => navigate(`/files/${id}`)}>
            {t('common.actions.cancel')}
          </Button>
        </Stack>
      </Stack>
    </AppShell>
  );
}
